/**
 * utils/siblings.js
 * Pure functions for managing sibling links — user-curated substitute relationships
 * between items. Mutual (A~B implies B~A) but NOT transitive (A~B and B~C does NOT
 * imply A~C). Stored as a flat pair collection (data.siblingLinks) so mutuality is
 * structural, not derived: each pair is read from either side via siblingsOf.
 *
 * Pair shape: { a, b } with a < b (canonical string ordering, via pairKey) — so
 * {A,B} and {B,A} are always the same stored pair and never duplicate.
 */

/** Canonical ordering for a pair of codes — sorted, so (A,B) and (B,A) agree. */
export function pairKey(a, b) {
  return a < b ? [a, b] : [b, a];
}

/** True if a and b are linked as siblings. A code is never its own sibling. */
export function areSiblings(data, a, b) {
  if (a === b) return false;
  const [x, y] = pairKey(a, b);
  return (data.siblingLinks || []).some((p) => p.a === x && p.b === y);
}

/** Partner codes for `code`, in siblingLinks insertion order, deduped. */
export function siblingsOf(data, code) {
  const out = [];
  const seen = new Set();
  for (const p of (data.siblingLinks || [])) {
    const partner = p.a === code ? p.b : (p.b === code ? p.a : null);
    if (partner != null && !seen.has(partner)) {
      seen.add(partner);
      out.push(partner);
    }
  }
  return out;
}

/**
 * Add a sibling link between a and b. No-op (same reference) when a===b, when
 * either code is falsy, or when the pair already exists. Does NOT verify the
 * items exist — that's the caller's job.
 */
export function linkSiblings(data, a, b) {
  if (!a || !b || a === b) return data;
  if (areSiblings(data, a, b)) return data;
  const [x, y] = pairKey(a, b);
  return { ...data, siblingLinks: [...(data.siblingLinks || []), { a: x, b: y }] };
}

/** Remove the sibling link between a and b. Same-reference no-op if the pair is absent. */
export function unlinkSiblings(data, a, b) {
  if (!a || !b) return data;
  const [x, y] = pairKey(a, b);
  const links = data.siblingLinks || [];
  const next = links.filter((p) => !(p.a === x && p.b === y));
  if (next.length === links.length) return data;
  return { ...data, siblingLinks: next };
}

/** Drop every pair containing code (item delete cleanup). Same-reference no-op if none. */
export function removeItemLinks(data, code) {
  const links = data.siblingLinks || [];
  const next = links.filter((p) => p.a !== code && p.b !== code);
  if (next.length === links.length) return data;
  return { ...data, siblingLinks: next };
}
